// Phase A (2026-08-20) — user self-serve security page.
// MFA enroll wizard + recovery codes + trusted devices + password change +
// login history.
import { useEffect, useState } from "react";
import {
  Shield, KeyRound, Smartphone, ScrollText, Trash2, RefreshCw,
  ShieldCheck, Copy, Download, AlertCircle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function MFAEnrollCard({ enrolled, onChange }) {
  const [step, setStep] = useState(enrolled ? "enrolled" : "idle");
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [otp, setOtp] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/mfa/setup-start", {});
      setQr(data.qr_png_base64); setSecret(data.secret); setStep("scan");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/mfa/setup-verify", { otp: otp.trim() });
      setCodes(data.recovery_codes || []);
      setStep("codes");
      onChange && onChange();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const copyCodes = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    toast.success("Recovery codes copied.");
  };
  const downloadCodes = () => {
    const blob = new Blob(
      ["SOM-TURK PLC — MFA Recovery Codes\n\n" + codes.join("\n") +
       "\n\nStore securely. Each code works ONCE.\n"], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "somturk-mfa-recovery-codes.txt";
    link.click();
  };

  if (step === "enrolled") {
    return (
      <Card className="p-5" data-testid="mfa-status-enrolled">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <div>
            <div className="font-semibold text-slate-800">
              Multi-Factor Authentication is ON
            </div>
            <div className="text-sm text-slate-500">
              Your account requires a 6-digit code on new devices.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 space-y-4" data-testid="mfa-enroll-card">
      <div className="flex items-center gap-3">
        <Shield className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Set up two-factor authentication</h3>
      </div>
      {err ? (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800
                        rounded-md px-3 py-2 text-sm" data-testid="mfa-enroll-err">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}
        </div>
      ) : null}
      {step === "idle" && (
        <>
          <p className="text-sm text-slate-600">
            Use an authenticator app such as Google Authenticator, 1Password, or
            Authy. Once enrolled, you'll only be prompted for a code when signing
            in from a new device.
          </p>
          <Button onClick={start} disabled={busy} data-testid="mfa-start">
            <Shield className="w-4 h-4 mr-2" />
            {busy ? "Preparing…" : "Begin setup"}
          </Button>
        </>
      )}
      {step === "scan" && (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="text-center">
            <img alt="QR to scan" src={`data:image/png;base64,${qr}`}
                 className="w-56 h-56 mx-auto border border-slate-200 rounded-md"
                 data-testid="mfa-qr" />
            <div className="text-xs text-slate-500 mt-2">Scan this in your app</div>
            <details className="mt-3 text-xs text-slate-500">
              <summary className="cursor-pointer">Can't scan? Show secret</summary>
              <div className="mt-2 font-mono text-slate-700 tracking-wider break-all"
                   data-testid="mfa-secret">{secret}</div>
            </details>
          </div>
          <div className="space-y-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">
              Enter the current 6-digit code
            </label>
            <Input inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                   value={otp}
                   onChange={e => setOtp(e.target.value.replace(/\D/g,"").slice(0,6))}
                   className="text-2xl tracking-[0.4em] text-center"
                   placeholder="123456"
                   data-testid="mfa-otp-input" />
            <Button onClick={verify} disabled={busy || otp.length !== 6}
                    data-testid="mfa-verify">
              {busy ? "Verifying…" : "Verify & Enable"}
            </Button>
          </div>
        </div>
      )}
      {step === "codes" && (
        <div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-900
                          rounded-md px-3 py-2 text-sm mb-4">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <b>Save these 10 recovery codes now.</b> Each one works ONCE and gets you
              back in if you lose your authenticator device.
              You will not be able to see them again.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50
                          border border-slate-200 rounded-md p-4"
               data-testid="mfa-recovery-codes">
            {codes.map(c => <div key={c} className="tracking-wider">{c}</div>)}
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={copyCodes} variant="outline"
                    data-testid="mfa-copy-codes">
              <Copy className="w-4 h-4 mr-2" /> Copy
            </Button>
            <Button onClick={downloadCodes} variant="outline"
                    data-testid="mfa-download-codes">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <div className="flex-1" />
            <Button onClick={() => { setStep("enrolled"); onChange && onChange(); }}
                    data-testid="mfa-done">Done</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function TrustedDevicesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setItems(data.devices || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  const revoke = async (id) => {
    if (!confirm("Revoke this device? You'll be signed out on that browser.")) return;
    await api.delete(`/auth/sessions/${id}`);
    refresh();
    toast.success("Device revoked.");
  };
  return (
    <Card className="p-5" data-testid="devices-card">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Trusted devices</h3>
        <Button size="sm" variant="ghost" onClick={refresh} data-testid="devices-refresh">
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>
      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm">No trusted devices yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map(d => (
            <div key={d.device_id}
                 className="flex items-center gap-3 border border-slate-200 rounded-md p-3"
                 data-testid={`device-${d.device_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 truncate">
                  {d.ua || "Unknown browser"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {d.ip || "—"} {d.country ? `· ${d.country}` : ""} ·
                  &nbsp;last seen {new Date(d.last_seen).toLocaleString()}
                </div>
              </div>
              {d.is_current && (
                <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                  this device
                </Badge>
              )}
              {d.revoked ? (
                <Badge variant="outline">revoked</Badge>
              ) : (
                <Button size="sm" variant="outline"
                        onClick={() => revoke(d.device_id)}
                        data-testid={`device-revoke-${d.device_id}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nx, setNx] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.post("/auth/security/change-password",
                     { current_password: cur, new_password: nx });
      setOk(true); setCur(""); setNx("");
      toast.success("Password changed.");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5" data-testid="change-pw-card">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Change password</h3>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            Current password
          </label>
          <Input type="password" value={cur} onChange={e => setCur(e.target.value)}
                 data-testid="cp-cur" required />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">
            New password (min 12 chars, 3 of upper/lower/digit/symbol)
          </label>
          <Input type="password" value={nx} onChange={e => setNx(e.target.value)}
                 data-testid="cp-new" required minLength={12} />
        </div>
        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200
                                 rounded px-3 py-2" data-testid="cp-err">{err}</div>}
        {ok && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200
                                rounded px-3 py-2" data-testid="cp-ok">Password changed.</div>}
        <Button type="submit" disabled={busy} data-testid="cp-submit">
          {busy ? "Saving…" : "Update password"}
        </Button>
      </form>
    </Card>
  );
}

function LoginHistoryCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/security/login-history");
        setRows(data.events || []);
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <Card className="p-5" data-testid="history-card">
      <div className="flex items-center gap-2 mb-3">
        <ScrollText className="w-5 h-5 text-slate-500" />
        <h3 className="text-slate-800 font-semibold">Recent login activity</h3>
      </div>
      {loading ? <div className="text-slate-500 text-sm">Loading…</div>
       : rows.length === 0 ? <div className="text-slate-500 text-sm">No history yet.</div>
       : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-500 uppercase text-xs">
              <tr>
                <th className="text-left px-2 py-1">When</th>
                <th className="text-left px-2 py-1">Result</th>
                <th className="text-left px-2 py-1">IP</th>
                <th className="text-left px-2 py-1">Location</th>
                <th className="text-left px-2 py-1">Method</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="px-2 py-1 text-xs whitespace-nowrap">
                    {new Date(r.at).toLocaleString()}
                  </td>
                  <td className="px-2 py-1">
                    {r.success
                      ? <span className="text-emerald-700 text-xs">success</span>
                      : <span className="text-red-700 text-xs">failed · {r.reason}</span>}
                  </td>
                  <td className="px-2 py-1 text-xs text-slate-500">{r.ip || "—"}</td>
                  <td className="px-2 py-1 text-xs text-slate-500">
                    {r.city || "—"}{r.country ? `, ${r.country}` : ""}
                  </td>
                  <td className="px-2 py-1 text-xs text-slate-500">
                    {r.method}{r.mfa_used ? " · MFA" : ""}{r.trusted_device ? " · trusted" : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function ProfileSecurityPage() {
  const { user, refresh } = useAuth();
  const [enrolled, setEnrolled] = useState(!!user?.mfa_enabled);
  const [meFresh, setMeFresh] = useState(user);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/auth/me");
      setMeFresh(data);
      setEnrolled(!!data.mfa_enabled);
    })();
  }, []);
  const graceRemaining = () => {
    if (!meFresh?.mfa_grace_expires_at) return null;
    const rem = new Date(meFresh.mfa_grace_expires_at) - new Date();
    if (rem <= 0) return "expired";
    const days = Math.max(0, Math.round(rem / (24 * 3600 * 1000)));
    return days;
  };
  const grace = graceRemaining();

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-security-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Security</h1>
        <p className="text-slate-500 text-sm">
          Manage MFA, trusted devices, password, and recent login activity.
        </p>
      </div>

      {!enrolled && grace !== null && grace !== "expired" && (
        <Card className="p-4 border-amber-300 bg-amber-50 text-amber-900"
              data-testid="mfa-grace-banner">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              <b>Set up MFA within {grace} day{grace === 1 ? "" : "s"}.</b>{" "}
              After that, sign-in will require enrollment.
            </div>
          </div>
        </Card>
      )}
      {!enrolled && grace === "expired" && (
        <Card className="p-4 border-red-300 bg-red-50 text-red-900" data-testid="mfa-grace-expired">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              <b>MFA setup is required to continue.</b>{" "}
              Enroll now to restore full access to the app.
            </div>
          </div>
        </Card>
      )}

      <MFAEnrollCard enrolled={enrolled}
                     onChange={async () => { setEnrolled(true); await refresh(); }} />
      <TrustedDevicesCard />
      <ChangePasswordCard />
      <LoginHistoryCard />
    </div>
  );
}
